import { Button } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import {
  clearButtonStyles
} from './styles';

type FormikDatePickerProp = {
  name: string;
  label: string;
  maxDate?: unknown;
  minDate?: unknown;
  id?: string;
  disabled?: boolean;
  helperText?: string;
  clearable?: boolean;
};

export function FormikDatePicker({
  name,
  label,
  maxDate,
  minDate,
  id,
  disabled,
  helperText = '',
  clearable,
}: FormikDatePickerProp): React.JSX.Element {
  const [field, meta] = useField({ name });
  const { value, touched, error } = meta;
  const shouldShowError = !!touched && !!error;
  const { onChange } = field;
  const hideDatePickerIcon = clearable && !!value && value !== '';

  const handleDateChange = useCallback(
    (value) => {
      onChange({ target: { name, value } });
    },
    [onChange, name]
  );

  const renderedDatepicker = useMemo(
    () => (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          value={field.value ? dayjs(field.value) : null}
          label={label}
          maxDate={maxDate ? dayjs(maxDate as string) : undefined}
          minDate={minDate ? dayjs(minDate as string) : undefined}
          onChange={handleDateChange}
          format={'DD/MM/YYYY'}
          disabled={disabled}
          name={name}
          slotProps={{
            textField: {
              id: id || name,
              name: name,
              error: shouldShowError,
              helperText: shouldShowError ? error : helperText,
              sx: {
                width: '100%'
              }
            }
          }}
          slots={{
            inputAdornment: hideDatePickerIcon ? () => (
              <Button onClick={() => handleDateChange(null)} sx={clearButtonStyles}>
                Clear
              </Button>
            ) : undefined
          }}
        />
      </LocalizationProvider>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      shouldShowError,
      error,
      name,
      disabled,
      helperText,
      id,
      label,
      maxDate,
      minDate,
      hideDatePickerIcon,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      JSON.stringify(field),
    ]
  );

  return renderedDatepicker;
}
